import { useState } from 'react'
import { useAddMember, useChannelMembers, useRemoveMember } from '../lib/queries'
import { toast } from '../lib/toast'
import Section from './ui/Section'
import Button from './ui/Button'
import Input from './ui/Input'
import Badge from './ui/Badge'
import ConfirmDialog from './ui/ConfirmDialog'

interface Props {
  channelId: string
  isOwner: boolean
}

export default function ModManager({ channelId, isOwner }: Props) {
  const { data: members = [] } = useChannelMembers(channelId)
  const addMember = useAddMember(channelId)
  const removeMember = useRemoveMember(channelId)

  const [email, setEmail] = useState('')
  const [confirmRemove, setConfirmRemove] = useState<{ id: string; email: string } | null>(null)

  async function add(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim()) return
    try {
      await addMember.mutateAsync(email.trim())
      toast.success('Mod eklendi')
      setEmail('')
    } catch (e) {
      toast.error((e as Error).message)
    }
  }

  return (
    <Section title="Üyeler" description={isOwner ? 'Mod ekleyip çıkarabilirsin' : 'Bu kanalın üyeleri'}>
      <ul className="space-y-1.5 mb-3">
        {members.map((m) => (
          <li key={m.user_id} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <span className="text-[var(--color-text)]">{m.email}</span>
              <Badge tone={m.role === 'owner' ? 'accent' : 'neutral'}>{m.role}</Badge>
            </div>
            {isOwner && m.role !== 'owner' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmRemove({ id: m.user_id, email: m.email })}
              >
                Çıkar
              </Button>
            )}
          </li>
        ))}
        {members.length === 0 && (
          <li className="text-xs text-[var(--color-text-subtle)]">Henüz üye yok.</li>
        )}
      </ul>
      {isOwner && (
        <form onSubmit={add} className="flex gap-2">
          <Input
            placeholder="email@..."
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="text-sm"
          />
          <Button type="submit" variant="primary" disabled={addMember.isPending} size="md">
            Ekle
          </Button>
        </form>
      )}

      <ConfirmDialog
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        onConfirm={async () => {
          if (!confirmRemove) return
          try {
            await removeMember.mutateAsync(confirmRemove.id)
            toast.success('Mod çıkarıldı')
          } catch (e) {
            toast.error((e as Error).message)
          }
        }}
        title="Modu çıkar?"
        description={confirmRemove ? `${confirmRemove.email} kanaldan çıkarılır.` : ''}
        confirmLabel="Çıkar"
        destructive
      />
    </Section>
  )
}
